import { type ReactNode, useId } from "react";
import { twJoin } from "tailwind-merge";

import { Stack } from "@/design-system/Stack";

/**
 * Frames a progressive form section. `isOpen` controls the content visibility, while `onOpen`
 * makes the heading an expand control. `isDisabled` prevents that control from opening the section,
 * `action` is shown beside a static heading, and `error` describes an unanswered section choice.
 */
export function InputFrame({
  title,
  description,
  isOpen = true,
  isDisabled = false,
  onOpen,
  toggleTestId,
  action,
  error,
  children,
}: {
  title: string;
  description?: string;
  isOpen?: boolean;
  isDisabled?: boolean;
  onOpen?: () => void;
  toggleTestId?: string;
  action?: ReactNode;
  error?: string;
  children: ReactNode;
}): ReactNode {
  const id = useId();
  const heading = (
    <Stack gap="xs">
      <span id={`${id}-title`} className="text-heading-3">
        {title}
      </span>
      {description && <span className="text-body">{description}</span>}
    </Stack>
  );

  return (
    <Stack gap="sm">
      <section
        aria-labelledby={`${id}-title`}
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={Boolean(error)}
        className={twJoin(
          "rounded-xl border-hairline border-border-default p-space-md transition-colors",
          isDisabled ? "bg-background-disabled text-text-disabled" : "bg-background-default text-text-primary",
          "focus-within:border-border-focus focus-within:ring-1 focus-within:ring-border-focus",
          !isDisabled && !isOpen && "hover:bg-background-default-hover",
        )}
      >
        <Stack gap="md">
          {onOpen ? (
            <button
              type="button"
              data-testid={toggleTestId}
              onClick={onOpen}
              disabled={isDisabled}
              aria-expanded={isOpen}
              aria-controls={`${id}-content`}
              className="w-full cursor-pointer text-left focus-visible:outline-hidden disabled:cursor-not-allowed"
            >
              {heading}
            </button>
          ) : (
            <header className="flex items-center justify-between gap-space-sm">
              {heading}
              {action}
            </header>
          )}
          {isOpen && <section id={`${id}-content`}>{children}</section>}
        </Stack>
      </section>
      {error && (
        <p id={`${id}-error`} role="alert" className="text-overline text-text-danger">
          {error}
        </p>
      )}
    </Stack>
  );
}
